import { useDeviceType, useToast } from '@cian/ui-kit';
import { useAppDispatch } from 'shared/hooks';
import { useSelector } from 'react-redux';

import * as React from 'react';
import { EDealType } from '../../../repositories/cpd/entities/v1_create_cpd_draft/PublicV1CreateCpdDraftRequest';

import { ChangeEvent } from 'react';
import { selectRegionsList } from '../../../selectors/form';
import { selectModalSource } from '../../../selectors/page';
import { getRegions, sendForm } from '../../../thunks';
import { formatPhoneStringToInput, isPhoneValid } from '../../../utils/formatPhoneString';
import { setModalFormClose } from '../../../slices/pageSlice';
import {
  DEAL_TYPE_OPTIONS,
  IDealTypeOption,
  IRegionOption,
  mapRegionsListToOptions,
} from '../../../constants/constants';
import { trackFormSend, trackFormClick } from '../tracking';
import { getUserPhone } from '../../../selectors/user';

export interface IFormProps {
  dealTypeValue?: EDealType[];
  dealTypeOptions: IDealTypeOption[];
  dealTypeChange: (value: EDealType) => void;
  phoneValue: string;
  onPhoneChange: (val: string) => void;
  regionValue?: number;
  regionOptions: IRegionOption[];
  onRegionChange: (val: number) => void;
  onSubmit: (draftSource: 'seoLandingPageForm' | 'seoLandingPageConsultation', customSource?: string) => void;
  legalAgreementAccepted: boolean;
  onLegalAgreementChange: (_: ChangeEvent<HTMLInputElement>, accepted: boolean) => void;
  source?: 'consultation' | 'ticket' | null;
  isDesktop?: boolean;
  errors?: {
    dealTypeError?: string;
    regionError?: string;
    phoneError?: string;
  };
}

export const useFormProps = (): IFormProps => {
  const deviceType = useDeviceType();
  const isDesktop = deviceType === 'desktop';

  const dispatch = useAppDispatch();
  const toastApi = useToast({ placement: 'top-right' });

  const regionList = useSelector(selectRegionsList);
  const source = useSelector(selectModalSource);

  const userPhone = useSelector(getUserPhone);

  const [dealType, setDealType] = React.useState<EDealType | undefined>(undefined);
  const [region, setRegion] = React.useState<number | undefined>(undefined);
  const [phoneValue, setPhoneValue] = React.useState(formatPhoneStringToInput(userPhone));
  const [legalAgreementAccepted, setLegalAgreementAccepted] = React.useState<boolean>(false);

  const [errors, setErrors] = React.useState<IFormProps['errors']>(undefined);

  const handleDealTypeChange = React.useCallback(
    async (newDealType: EDealType) => {
      setErrors(e => ({
        ...e,
        dealTypeError: undefined,
      }));
      setDealType(newDealType);

      await dispatch(getRegions(newDealType));
    },
    [dispatch],
  );

  const handleRegionChange = React.useCallback((newRegion: number) => {
    setErrors(e => ({
      ...e,
      regionError: undefined,
    }));
    setRegion(newRegion);
  }, []);

  const handlePhoneChange = React.useCallback((value: string) => {
    setErrors(e => ({
      ...e,
      phoneError: undefined,
    }));
    setPhoneValue(value);
  }, []);

  const handleLegalAgreementChange = React.useCallback((_: ChangeEvent<HTMLInputElement>, accepted: boolean) => {
    setLegalAgreementAccepted(accepted);
  }, []);

  const handleSubmit = React.useCallback(
    async (draftSource: 'seoLandingPageForm' | 'seoLandingPageConsultation', customSource?: string) => {
      const isFormValid = (): boolean => {
        if (!dealType) {
          setErrors(e => ({
            ...e,
            dealTypeError: 'Выберите тип услуги',
          }));

          return false;
        }

        if (!region) {
          setErrors(e => ({
            ...e,
            regionError: 'Выберите регион',
          }));

          return false;
        }

        if (!isPhoneValid(phoneValue)) {
          setErrors(e => ({
            ...e,
            phoneError: 'Введите корректный номер телефона',
          }));

          return false;
        }

        return true;
      };

      if (isFormValid()) {
        setErrors(undefined);
        trackFormClick({
          source,
          customSource,
          dealType: dealType as EDealType,
          regionId: region as number,
        });

        const res = await dispatch(
          sendForm({
            source: draftSource,
            dealType: dealType as EDealType,
            regionId: region as number,
            phone: phoneValue,
            legalAgreementAccepted,
          }),
        );

        if (res.payload) {
          trackFormSend(res.payload as number, customSource);
          dispatch(setModalFormClose());
          toastApi.open({ type: 'success', text: 'Скоро свяжемся с вами' });
        } else {
          toastApi.open({ type: 'error', text: 'Что-то пошло не так — попробуйте ещё раз' });
        }
      }
    },
    [dispatch, dealType, region, phoneValue, source, legalAgreementAccepted, toastApi],
  );

  return {
    dealTypeValue: dealType ? [dealType] : undefined,
    dealTypeOptions: DEAL_TYPE_OPTIONS,
    dealTypeChange: handleDealTypeChange,
    phoneValue,
    onPhoneChange: handlePhoneChange,
    regionValue: region,
    regionOptions: mapRegionsListToOptions(regionList),
    onRegionChange: handleRegionChange,
    onSubmit: handleSubmit,
    legalAgreementAccepted,
    onLegalAgreementChange: handleLegalAgreementChange,
    source,
    isDesktop,
    errors,
  };
};
